"use client";
import React, {
  useState,
  useRef,
  KeyboardEvent,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import {
  Block,
  BlockType,
  TimeOfDay,
  uuid,
  blockTypes,
} from "../types/screenplay-editor.types";
import * as ScreenplayService from "../service/screenplay-editor.service";
import { comparePositions } from "../service/fractional-indexing";
import {
  LanguageType,
  getMappedCharacter,
  LANGUAGE_CONFIGS,
  PHONETIC_SEQUENCES,
  PhoneticEngine,
  PhoneticState,
  getRelatedLetters,
  isIndicConsonant,
} from "../service/multilingual-input.service";

const initialBlocks: Block[] = [
  {
    id: "initscene",
    type: "SCENE_HEADING",
    text: "",
    position: "m_initscene",
    sceneNumber: "1",
    sceneType: "INT.",
    timeOfDay: "DAY",
  },
];

function getCaretCharacterOffsetWithin(element: HTMLElement) {
  let caretOffset = 0;
  const doc = element.ownerDocument;
  const win = doc.defaultView;
  const sel = win?.getSelection();
  if (sel && sel.rangeCount > 0) {
    const range = sel.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(element);
    preCaretRange.setEnd(range.endContainer, range.endOffset);
    caretOffset = preCaretRange.toString().length;
  }
  return caretOffset;
}

function setCaretPosition(element: HTMLElement, offset: number) {
  const sel = window.getSelection();
  if (!sel) return;
  const range = document.createRange();
  let currentOffset = 0;
  let found = false;

  function traverseNodes(node: Node) {
    if (found) return;
    if (node.nodeType === Node.TEXT_NODE) {
      const length = node.nodeValue?.length || 0;
      if (currentOffset + length >= offset) {
        range.setStart(node, offset - currentOffset);
        range.collapse(true);
        found = true;
      } else {
        currentOffset += length;
      }
    } else {
      for (let i = 0; i < node.childNodes.length; i++) {
        traverseNodes(node.childNodes[i]);
        if (found) break;
      }
    }
  }

  traverseNodes(element);

  if (!found) {
    range.setStart(element, 0);
    range.collapse(true);
  }

  sel.removeAllRanges();
  sel.addRange(range);
}

function createRangeFromGlobalOffset(
  element: HTMLElement,
  startOffset: number,
  endOffset: number,
): Range | null {
  const range = document.createRange();
  let currentOffset = 0;
  let startNode: Node | null = null;
  let startLocalOffset = 0;
  let endNode: Node | null = null;
  let endLocalOffset = 0;

  function traverseNodes(node: Node) {
    if (endNode) return;

    if (node.nodeType === Node.TEXT_NODE) {
      const length = node.nodeValue?.length || 0;

      if (!startNode && currentOffset + length >= startOffset) {
        startNode = node;
        startLocalOffset = startOffset - currentOffset;
      }

      if (currentOffset + length >= endOffset) {
        endNode = node;
        endLocalOffset = endOffset - currentOffset;
      }

      currentOffset += length;
    } else {
      for (let i = 0; i < node.childNodes.length; i++) {
        traverseNodes(node.childNodes[i]);
      }
    }
  }

  traverseNodes(element);

  if (startNode && endNode) {
    range.setStart(startNode, startLocalOffset);
    range.setEnd(endNode, endLocalOffset);
    return range;
  }

  return null;
}

export interface UseScreenplayEditorOptions {
  script_id?: string;
  blocks?: Block[];
  initialUrl?: string;
  fetchOptions?: RequestInit;
  onSave?: (blocks: Block[]) => void;
  onSaveBlob?: (content?: Blob) => void;
  onSyncWithCloud?: (
    blocks: Block[],
    sceneNumbers: Record<string, string>,
  ) => void;
  enhanceContentUrl?: string | URL | Request;
  isLocked?: boolean;
}

export function useScreenplayEditor(options?: UseScreenplayEditorOptions) {
  const [blocks, setBlocks] = useState<Block[]>(() => {
    const raw = options?.blocks || initialBlocks;
    return ScreenplayService.assignInitialPositions(raw);
  });

  const hasInitializedRef = useRef(
    !!(options?.blocks && options.blocks.length > 0),
  );
  useEffect(() => {
    if (
      options?.blocks &&
      options.blocks.length > 0 &&
      !hasInitializedRef.current
    ) {
      setIsLoading(true);
      isWaitingForDOMRef.current = true;
      skipAutosaveRef.current = true;
      const timer = setTimeout(() => {
        setBlocks(ScreenplayService.assignInitialPositions(options.blocks!));
        hasInitializedRef.current = true;
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [options?.blocks]);

  const refs = useRef<Record<string, HTMLDivElement | null>>({});
  const [focusedBlockId, setFocusedBlockId] = useState<string>(
    initialBlocks[0].id,
  );
  const [newBlockId, setNewBlockId] = useState<string | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showExtensionSuggestions, setShowExtensionSuggestions] =
    useState(false);
  const [isLoading, setIsLoading] = useState(
    !!(options?.initialUrl || (options?.blocks && options.blocks.length > 0)),
  );
  const [enhancingBlockId, setEnhancingBlockId] = useState<string | null>(null);
  const [enhancementSuggestion, setEnhancementSuggestion] = useState<
    string | null
  >(null);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedPopover, setShowUnsavedPopover] = useState(false);
  const popoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const blurTimeout = useRef<NodeJS.Timeout | null>(null);
  const isInitialLoadRef = useRef(true);
  const skipAutosaveRef = useRef(false);
  const isWaitingForDOMRef = useRef(
    !!(options?.initialUrl || (options?.blocks && options.blocks.length > 0)),
  );
  const loadedUrlRef = useRef<string | null>(null);
  const lastSavedContent = useRef<string | null>(null);
  const onSaveRef = useRef(options?.onSave);
  const onSaveBlobRef = useRef(options?.onSaveBlob);
  const onSyncWithCloudRef = useRef(options?.onSyncWithCloud);
  const [autosaveStatus, setAutosaveStatus] = useState<
    "idle" | "typing" | "saving" | "saved" | "error"
  >("idle");
  const isLocked = options?.isLocked ?? false;

  const [currentLanguage, setCurrentLanguage] =
    useState<LanguageType>("English");
  const [lastPhoneticAction, setLastPhoneticAction] = useState<
    (PhoneticState & { blockId: string }) | null
  >(null);
  const [phoneticSuggestions, setPhoneticSuggestions] = useState<string[]>([]);
  const lastSyncedTextRef = useRef<Record<string, string>>({});

  useEffect(() => {
    setLastPhoneticAction(null);
    setPhoneticSuggestions([]);
  }, [currentLanguage]);

  const phoneticEngine = useMemo(() => {
    if (currentLanguage.includes("Phonetic")) {
      return new PhoneticEngine(currentLanguage);
    }
    return null;
  }, [currentLanguage]);

  useEffect(() => {
    onSaveRef.current = options?.onSave;
    onSaveBlobRef.current = options?.onSaveBlob;
    onSyncWithCloudRef.current = options?.onSyncWithCloud;
  }, [options?.onSave, options?.onSaveBlob, options?.onSyncWithCloud]);

  const isFirstAutosaveRender = useRef(true);
  const prevBlocksRef = useRef<Block[]>(blocks);

  useEffect(() => {
    // If screenplay is locked, we do not perform autosaves
    if (isLocked) {
      setAutosaveStatus("idle");
      return;
    }

    // Skip the first render or initial load to avoid autosaving initial state
    if (isFirstAutosaveRender.current) {
      isFirstAutosaveRender.current = false;
      prevBlocksRef.current = blocks;
      return;
    }

    if (skipAutosaveRef.current) {
      skipAutosaveRef.current = false;
      prevBlocksRef.current = blocks;
      return;
    }

    // High performance shallow-reference comparison of blocks array
    const hasChanged =
      blocks.length !== prevBlocksRef.current.length ||
      blocks.some((b, i) => b !== prevBlocksRef.current[i]);

    if (!hasChanged) {
      return;
    }

    // Set status to typing as user is making changes
    setAutosaveStatus("typing");
    prevBlocksRef.current = blocks;

    // Trigger a 3-second debounced autosave of JSON Blocks
    const timer = setTimeout(async () => {
      if (onSaveRef.current) {
        setAutosaveStatus("saving");
        try {
          await onSaveRef.current(blocks);
          // Wait for 800ms to allow saving spinner to display beautifully in the UI
          await new Promise((resolve) => setTimeout(resolve, 800));
          setAutosaveStatus("saved");
          setTimeout(() => {
            setAutosaveStatus((prev) => (prev === "saved" ? "idle" : prev));
          }, 2000);
        } catch (error) {
          console.error("Autosave failed:", error);
          setAutosaveStatus("error");
        }
      } else {
        setAutosaveStatus("idle");
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [blocks, isLocked]);

  const latestBlocksRef = useRef(blocks);
  useEffect(() => {
    latestBlocksRef.current = blocks;
  }, [blocks]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (autosaveStatus === "typing" || autosaveStatus === "saving") {
        e.preventDefault();
        e.returnValue = "Changes you made may not be saved.";
        return e.returnValue;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [autosaveStatus]);

  const autosaveStatusRef = useRef(autosaveStatus);
  useEffect(() => {
    autosaveStatusRef.current = autosaveStatus;
  }, [autosaveStatus]);

  const showExtensionSuggestionsRef = useRef(showExtensionSuggestions);
  useEffect(() => {
    showExtensionSuggestionsRef.current = showExtensionSuggestions;
  }, [showExtensionSuggestions]);

  const lastPhoneticActionRef = useRef(lastPhoneticAction);
  useEffect(() => {
    lastPhoneticActionRef.current = lastPhoneticAction;
  }, [lastPhoneticAction]);

  useEffect(() => {
    return () => {
      // Flush any pending database saving on unmount
      if (autosaveStatusRef.current === "typing" && onSaveRef.current) {
        onSaveRef.current(latestBlocksRef.current);
      }
    };
  }, []);

  const characterExtensions = useMemo(
    () => ["(V.O.)", "(O.S.)", "(O.C.)", "(SUBTITLE)", "(CONT'D)"],
    [],
  );

  const handleSceneNumberChange = useCallback(
    (id: string, newNumber: string) => {
      setBlocks((prevBlocks) =>
        prevBlocks.map((block) =>
          block.id === id
            ? { ...block, sceneNumber: newNumber.toUpperCase() }
            : block,
        ),
      );
    },
    [],
  );

  // Derived State (Highly optimized single-pass loop)
  const { locations, characters, sceneNumbers } = useMemo(() => {
    const locsSet = new Set<string>();
    const charsSet = new Set<string>();
    const sceneNumMap: Record<string, string> = {};
    let fallbackCount = 0;

    blocks.forEach((block) => {
      const textTrim = block.text.trim();
      if (block.type === "SCENE_HEADING") {
        if (textTrim !== "") {
          locsSet.add(textTrim.toUpperCase());
        }
        if (block.sceneNumber) {
          sceneNumMap[block.id] = block.sceneNumber;
          const base = parseInt(block.sceneNumber);
          if (!isNaN(base)) fallbackCount = Math.max(fallbackCount, base);
        } else {
          fallbackCount++;
          sceneNumMap[block.id] = String(fallbackCount);
        }
      } else if (block.type === "CHARACTER") {
        if (textTrim !== "") {
          const textUpper = textTrim.toUpperCase();
          const parenIndex = textUpper.indexOf("(");
          if (parenIndex > -1) {
            const charName = textUpper.substring(0, parenIndex).trim();
            if (charName) charsSet.add(charName);
          } else {
            charsSet.add(textUpper);
          }
        }
      }
    });

    return {
      locations: Array.from(locsSet),
      characters: Array.from(charsSet),
      sceneNumbers: sceneNumMap,
    };
  }, [blocks]);

  useEffect(() => {
    if (newBlockId && refs.current[newBlockId]) {
      const block = blocks.find((b) => b.id === newBlockId);
      const el = refs.current[newBlockId];
      if (el && block) {
        el.focus();
        el.textContent = block.text;
        lastSyncedTextRef.current[newBlockId] = block.text;
        if (block.type === "PARENTHETICAL") {
          setTimeout(() => setCaretPosition(el, 1), 0);
        } else {
          setTimeout(() => setCaretPosition(el, block.text.length), 0);
        }
      }
      setFocusedBlockId(newBlockId);
      setNewBlockId(null);
    }
  }, [newBlockId, blocks]);

  useEffect(() => {
    blocks.forEach((block) => {
      const element = refs.current[block.id];
      if (element) {
        if (
          lastSyncedTextRef.current[block.id] !== block.text &&
          document.activeElement !== element
        ) {
          element.textContent = block.text;
          lastSyncedTextRef.current[block.id] = block.text;
        }
      }
    });

    if (isWaitingForDOMRef.current) {
      const dismissLoader = () => {
        if (typeof window !== "undefined") {
          // Force layout calculation one final time to settle the DOM dimensions
          void document.body.scrollHeight;
        }
        setIsLoading(false);
        isWaitingForDOMRef.current = false;
      };

      // We wait until:
      // 1. All web fonts (Courier Prime) and stylesheets are 100% loaded and applied.
      // 2. The main thread has finished parsing, DOM rendering, and styling, and is fully idle.
      const waitForAllReady = async () => {
        if (typeof window !== "undefined") {
          try {
            if (document.fonts && document.fonts.ready) {
              await document.fonts.ready;
            }
          } catch (e) {
            console.error("Font loading detection failed:", e);
          }
        }

        if (typeof window !== "undefined" && "requestIdleCallback" in window) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              window.requestIdleCallback(
                () => {
                  // Give it a tiny 150ms breathing window to completely settle
                  setTimeout(dismissLoader, 150);
                },
                { timeout: 2000 },
              ); // Max fallback of 2 seconds
            });
          });
        } else {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              setTimeout(dismissLoader, 400);
            });
          });
        }
      };

      waitForAllReady();
    }
  }, [blocks]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      // Check if the clicked element is a contentEditable block or inside the toolbar
      const target = e.target as HTMLElement;
      const isInsideBlock = target.closest("[data-block-id]");
      const isInsideToolbar = target.closest(".sticky"); // Adjust selector based on your UI
      const isInsideSuggestions = target.closest('[role="listbox"]');
      const isInsideEditorSheet = target.closest(
        '[data-screenplay-editor="true"]',
      );

      if (!isInsideBlock && !isInsideToolbar && !isInsideSuggestions) {
        if (!isInsideEditorSheet) {
          setFocusedBlockId((prev) => (prev !== "" ? "" : prev));
          setShowSuggestions((prev) => (prev !== false ? false : prev));
          setShowExtensionSuggestions((prev) =>
            prev !== false ? false : prev,
          );
          setPhoneticSuggestions((prev) => (prev.length > 0 ? [] : prev));
        } else {
          // Notion-style Canvas Whitespace Appending (8th point of docs/collaboration_research.md)
          // If they click inside the sheet container, but below the last block visually:
          if (latestBlocksRef.current.length > 0) {
            const sorted = [...latestBlocksRef.current].sort((a, b) =>
              comparePositions(a.position, b.position),
            );
            const lastBlock = sorted[sorted.length - 1];
            const lastBlockEl = refs.current[lastBlock.id];
            if (lastBlockEl) {
              const rect = lastBlockEl.getBoundingClientRect();
              if (e.clientY > rect.bottom && !isLocked) {
                // User clicked below the last block! Let's append an empty block at the end
                const { newBlocks, newBlockId } =
                  ScreenplayService.addBlockAfter(
                    latestBlocksRef.current,
                    lastBlock.id,
                  );
                setBlocks(newBlocks);
                setNewBlockId(newBlockId);
              }
            }
          }
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isLocked]);

  // Handlers
  const handleBlockTextChange = useCallback((id: string, text: string) => {
    const block = latestBlocksRef.current.find((b) => b.id === id);
    if (!block) return;

    if (block.type === "CHARACTER") {
      const trimmedText = text.trim();
      const openParenIndex = trimmedText.lastIndexOf("(");
      const closeParenIndex = trimmedText.lastIndexOf(")");

      if (openParenIndex !== -1 && openParenIndex > closeParenIndex) {
        setShowExtensionSuggestions(true);
        setShowSuggestions(false);
      } else {
        setShowExtensionSuggestions(false);
        setShowSuggestions(openParenIndex === -1);
      }
    } else if (showExtensionSuggestionsRef.current) {
      setShowExtensionSuggestions(false);
    }

    let processedText = text;
    if (block.type === "PARENTHETICAL") {
      const clean = text.replace(/[()]/g, "");
      if (!clean && text === "") {
        processedText = "";
      } else {
        processedText = `(${clean})`;
      }
    }

    setBlocks((bs) =>
      ScreenplayService.updateBlock(bs, id, "text", processedText),
    );

    if (text !== processedText) {
      const el = refs.current[id];
      if (el) {
        const offset = getCaretCharacterOffsetWithin(el);
        const charsBeforeCaret = text
          .substring(0, offset)
          .replace(/[()]/g, "").length;
        const newOffset = 1 + charsBeforeCaret;
        el.textContent = processedText;
        lastSyncedTextRef.current[id] = processedText;
        setCaretPosition(el, newOffset);
      }
    } else {
      lastSyncedTextRef.current[id] = text;
    }
  }, []);

  const handleSceneTypeChange = useCallback(
    (id: string, sceneType: "INT." | "EXT." | "INT/EXT.") => {
      setBlocks((bs) =>
        ScreenplayService.updateBlock(bs, id, "sceneType", sceneType),
      );
      setTimeout(() => {
        refs.current[id]?.focus();
        setFocusedBlockId(id);
      }, 10);
    },
    [],
  );

  const handleTimeOfDayChange = useCallback((id: string, time: TimeOfDay) => {
    setBlocks((bs) => ScreenplayService.updateBlock(bs, id, "timeOfDay", time));
    setTimeout(() => {
      refs.current[id]?.focus();
      setFocusedBlockId(id);
    }, 10);
  }, []);

  const handleBlockTypeChange = useCallback(
    (newType: BlockType) => {
      if (!focusedBlockId) return;

      // The service now handles text preservation
      setBlocks((bs) =>
        ScreenplayService.changeBlockType(bs, focusedBlockId, newType),
      );

      setTimeout(() => {
        const el = refs.current[focusedBlockId];
        if (el) {
          // Find the block from the latest state to get the correctly formatted text
          setBlocks((currentBlocks) => {
            const currentBlock = currentBlocks.find(
              (b) => b.id === focusedBlockId,
            );
            if (currentBlock && el.innerText !== currentBlock.text) {
              el.innerText = currentBlock.text;
            }

            el.focus();
            const pos =
              newType === "PARENTHETICAL"
                ? el.innerText.length - 1
                : el.innerText.length;
            setCaretPosition(el, Math.max(0, pos));

            return currentBlocks;
          });
        }
      }, 10);
    },
    [focusedBlockId, blocks],
  );

  const handleSelectCharacterExtension = useCallback(
    (extension: string) => {
      if (!focusedBlockId) return;

      setBlocks((currentBlocks) => {
        const block = currentBlocks.find((b) => b.id === focusedBlockId);
        if (!block || block.type !== "CHARACTER") return currentBlocks;

        const parenIndex = block.text.indexOf("(");
        const baseText = (
          parenIndex > -1 ? block.text.substring(0, parenIndex) : block.text
        ).trim();
        const newText = `${baseText} ${extension}`;

        const newBlocks = ScreenplayService.updateBlock(
          currentBlocks,
          focusedBlockId,
          "text",
          newText,
        );

        setTimeout(() => {
          const el = refs.current[focusedBlockId];
          if (el) {
            el.innerText = newText;
            el.focus();
            setCaretPosition(el, newText.length);
          }
        }, 0);
        return newBlocks;
      });
      setShowExtensionSuggestions(false);
    },
    [focusedBlockId],
  );

  const focusBlock = (id: string, position: "start" | "end" = "start") => {
    const el = refs.current[id];
    if (!el) return;

    el.focus();

    const range = document.createRange();
    const sel = window.getSelection();
    if (!sel) return;

    range.selectNodeContents(el);
    range.collapse(position === "start");

    sel.removeAllRanges();
    sel.addRange(range);
  };

  const cycleBlockType = (id: string, direction: "up" | "down") => {
    const block = latestBlocksRef.current.find((b) => b.id === id);
    if (!block) return;

    const idx = blockTypes.indexOf(block.type);
    let newIdx = direction === "up" ? idx - 1 : idx + 1;
    if (newIdx < 0) newIdx = blockTypes.length - 1;
    if (newIdx >= blockTypes.length) newIdx = 0;

    const newType = blockTypes[newIdx];

    setBlocks((bs) => ScreenplayService.changeBlockType(bs, id, newType));

    // Handle the DOM and Caret after the state update
    setTimeout(() => {
      const el = refs.current[id];
      if (el) {
        setBlocks((currentBlocks) => {
          const currentBlock = currentBlocks.find((b) => b.id === id);
          if (currentBlock && el.innerText !== currentBlock.text) {
            el.innerText = currentBlock.text;
          }

          el.focus();
          if (newType === "PARENTHETICAL") {
            setCaretPosition(el, 1);
          } else {
            setCaretPosition(el, el.innerText.length);
          }

          return currentBlocks;
        });
      }
    }, 10);
  };

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>, id: string, text: string) => {
      if (isLocked) return;
      // 1. Multilingual Input Support (DFSM)
      if (currentLanguage !== "English") {
        const config = LANGUAGE_CONFIGS[currentLanguage];
        const char = getMappedCharacter(
          currentLanguage,
          e.code,
          e.shiftKey,
          e.getModifierState("AltGraph") || e.altKey,
        );

        if (e.key === "Escape") {
          setPhoneticSuggestions([]);
          return;
        }

        // Handle numeric selection for phonetic suggestions with Alt key to avoid interrupting normal typing
        if (
          phoneticSuggestions.length > 0 &&
          e.altKey &&
          e.key >= "1" &&
          e.key <= String(phoneticSuggestions.length)
        ) {
          e.preventDefault();
          const suggestion = phoneticSuggestions[parseInt(e.key) - 1];
          handleSelectPhoneticSuggestion(suggestion);
          return;
        }

        // Clear suggestions if any other key is pressed (except modifiers)
        if (
          !e.altKey &&
          !e.ctrlKey &&
          !e.shiftKey &&
          !e.metaKey &&
          e.key !== "Alt" &&
          e.key !== "Control" &&
          e.key !== "Shift"
        ) {
          // We'll clear them after processing the current key if it doesn't generate new suggestions
        }

        // Only intercept if we have a mapping and no meta keys are pressed
        if (char && !e.ctrlKey && !e.metaKey) {
          e.preventDefault();
          const el = e.currentTarget;

          // Google-Standard Phonetic Logic (Engine Delegated)
          if (config.isPhonetic && phoneticEngine) {
            const rawKey = e.key;
            const offset = getCaretCharacterOffsetWithin(el);
            const textBefore = el.innerText.substring(0, offset);
            const prevChar = textBefore.slice(-1);
            const isDifferentBlock =
              lastPhoneticActionRef.current?.blockId !== id;

            // We store the last offset to detect manual cursor movement
            // Creating a simple heuristic: if the engine didn't just inert text here, reset.
            const engineState = isDifferentBlock
              ? null
              : lastPhoneticActionRef.current;

            const action = phoneticEngine.getAction(
              rawKey,
              e.code,
              prevChar,
              engineState,
            );

            if (action) {
              const selection = window.getSelection();
              if (selection) {
                // Surgical Replacement
                if (action.replaceCount > 0) {
                  const targetRange = createRangeFromGlobalOffset(
                    el,
                    offset - action.replaceCount,
                    offset,
                  );
                  if (targetRange) {
                    selection.removeAllRanges();
                    selection.addRange(targetRange);
                    // Explicitly delete to force immediate DOM change before insert
                    document.execCommand("delete", false);
                  }
                }

                document.execCommand("insertText", false, action.insertchar);
                setLastPhoneticAction({ ...action.newState, blockId: id });
                handleBlockTextChange(id, el.innerText);

                // Update phonetic suggestions
                const script = currentLanguage.includes("Hindi")
                  ? "Hindi"
                  : "Gujarati";
                const related = getRelatedLetters(action.insertchar, script);
                setPhoneticSuggestions([action.insertchar, ...related]);
                return;
              }
            }

            // Fallback for non-rule keys (ensure state is updated)
            const mappedFallback = char;
            setLastPhoneticAction({
              type: isIndicConsonant(mappedFallback) ? "consonant" : "other",
              char: rawKey.toLowerCase(),
              blockId: id,
            });

            document.execCommand("insertText", false, mappedFallback);
            handleBlockTextChange(id, el.innerText);

            const script = currentLanguage.includes("Hindi")
              ? "Hindi"
              : "Gujarati";
            const related = getRelatedLetters(mappedFallback, script);
            setPhoneticSuggestions([mappedFallback, ...related]);
            return;
          }

          document.execCommand("insertText", false, char);
          handleBlockTextChange(id, el.innerText);
          setPhoneticSuggestions([]); // Clear suggestions on direct mapping
          return;
        } else {
          // If no mapping found for this key, clear suggestions
          setPhoneticSuggestions([]);
        }
      }

      const block = latestBlocksRef.current.find((b) => b.id === id);

      if (block?.type === "PARENTHETICAL") {
        const offset = getCaretCharacterOffsetWithin(e.currentTarget);

        if (e.key === "Backspace" && (offset <= 1 || text === "()")) {
          e.preventDefault();
          cycleBlockType(id, "up");
          return;
        }
        if (
          e.key === "Delete" &&
          (offset >= text.length - 1 || text === "()")
        ) {
          e.preventDefault();
          return;
        }
      }

      const isActuallyEmpty = !text || text.trim() === "" || text === "\n";
      if (
        (e.key === "Backspace" || e.key === "Delete") &&
        isActuallyEmpty &&
        latestBlocksRef.current.length > 1
      ) {
        e.preventDefault();
        const { newBlocks, nextFocusedId } = ScreenplayService.deleteBlock(
          latestBlocksRef.current,
          id,
        );
        setBlocks(newBlocks);
        if (nextFocusedId) {
          setTimeout(() => {
            focusBlock(nextFocusedId, "end");
          }, 0);
        }
        return;
      }

      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        const { newBlocks, newBlockId } = ScreenplayService.addBlockAfter(
          latestBlocksRef.current,
          id,
        );
        setBlocks(newBlocks);
        setNewBlockId(newBlockId);
      } else if (e.key === "ArrowUp" && e.ctrlKey) {
        e.preventDefault();
        cycleBlockType(id, "up");
      } else if (e.key === "ArrowDown" && e.ctrlKey) {
        e.preventDefault();
        cycleBlockType(id, "down");
      } else if (e.key === "ArrowUp" && !e.ctrlKey) {
        const selection = window.getSelection();
        if (
          !selection ||
          !selection.isCollapsed ||
          selection.rangeCount === 0
        ) {
          return;
        }

        const range = selection.getRangeAt(0);
        const contentEditable = e.currentTarget as HTMLDivElement;

        let atStart = false;
        if (range.startOffset === 0) {
          let node = range.startContainer;
          if (node === contentEditable) {
            atStart = true;
          } else {
            let atStartOfParents = true;
            while (node !== contentEditable) {
              if (node.previousSibling) {
                atStartOfParents = false;
                break;
              }
              node = node.parentNode!;
              if (!node) {
                atStartOfParents = false;
                break;
              }
            }
            atStart = atStartOfParents;
          }
        }

        if (atStart) {
          e.preventDefault();
          const currentIndex = latestBlocksRef.current.findIndex(
            (b) => b.id === id,
          );
          if (currentIndex > 0) {
            const prevBlockId = latestBlocksRef.current[currentIndex - 1].id;
            focusBlock(prevBlockId, "end");
          }
        }
      } else if (e.key === "ArrowDown" && !e.ctrlKey) {
        const selection = window.getSelection();
        if (
          !selection ||
          !selection.isCollapsed ||
          selection.rangeCount === 0
        ) {
          return;
        }

        const range = selection.getRangeAt(0);
        const contentEditable = e.currentTarget as HTMLDivElement;
        const container = range.endContainer;

        const isAtEndOffset =
          range.endOffset ===
          (container.nodeType === Node.TEXT_NODE
            ? container.textContent?.length
            : container.childNodes.length);

        if (isAtEndOffset) {
          let atEnd = false;
          let node = container;
          if (node === contentEditable) {
            atEnd = true;
          } else {
            let atEndOfParents = true;
            while (node !== contentEditable) {
              if (node.nextSibling) {
                atEndOfParents = false;
                break;
              }
              node = node.parentNode!;
              if (!node) {
                atEndOfParents = false;
                break;
              }
            }
            atEnd = atEndOfParents;
          }

          if (atEnd) {
            e.preventDefault();
            const currentIndex = latestBlocksRef.current.findIndex(
              (b) => b.id === id,
            );
            if (currentIndex < latestBlocksRef.current.length - 1) {
              const nextBlockId = latestBlocksRef.current[currentIndex + 1].id;
              focusBlock(nextBlockId, "start");
            }
          }
        }
      }
    },
    [handleBlockTextChange, currentLanguage, phoneticEngine, isLocked],
  );

  const handleScriptImport = useCallback(
    (
      title: string,
      content: string,
      preParsedBlocks?: Partial<Block>[],
      isInitialLoad?: boolean,
    ) => {
      let parsedBlocks: Block[] = [];

      // Directly use provided blocks (like from an SBX file) or parse standard text
      if (preParsedBlocks && preParsedBlocks.length > 0) {
        parsedBlocks = preParsedBlocks.map((b) => ({
          id: b.id || uuid(),
          type: b.type || "ACTION",
          text: b.text || "",
          sceneNumber: b.sceneNumber,
          sceneType: b.sceneType,
          timeOfDay: b.timeOfDay,
        })) as Block[];
      } else {
        parsedBlocks = ScreenplayService.parseScreenplayText(content);
      }

      if (parsedBlocks.length > 0) {
        setIsLoading(true);
        isWaitingForDOMRef.current = true;
        skipAutosaveRef.current = true;
        let fallbackCount = 1;

        const finalizedBlocks = parsedBlocks.map((block) => {
          if (block.type === "SCENE_HEADING") {
            // RULE 1: If the parser found ANY string (24A, 1, 10-B), USE IT.
            // We do zero math on it.
            if (block.sceneNumber && block.sceneNumber.trim().length > 0) {
              // Only update our fallback counter if we see a plain number (e.g. "24")
              // If we see "24A", we DON'T update the count so the next scene stays on track.
              const isPureNumber = /^\d+$/.test(block.sceneNumber);
              if (isPureNumber) {
                fallbackCount = parseInt(block.sceneNumber) + 1;
              }

              return block;
            }

            // RULE 2: Fallback ONLY for scenes that had no number in the script
            return { ...block, sceneNumber: String(fallbackCount++) };
          }
          return block;
        });

        setTimeout(() => {
          setBlocks(ScreenplayService.assignInitialPositions(finalizedBlocks));

          if (onSaveBlobRef.current) {
            const sbxData = ScreenplayService.serializeToSbx(finalizedBlocks);

            // Only save if the data is different from what we last saved
            if (sbxData !== lastSavedContent.current) {
              lastSavedContent.current = sbxData;
              if (!isInitialLoad) {
                const blob = new Blob([sbxData], { type: "text/plain" });
                onSaveBlobRef.current(blob);
              }
            }
          }

          if (onSaveRef.current && !isInitialLoad) {
            onSaveRef.current(finalizedBlocks);
          }

          // 3. Focus logic
          setTimeout(() => {
            const firstId = parsedBlocks[0].id;
            if (firstId && refs.current[firstId]) {
              setFocusedBlockId(firstId);
              refs.current[firstId]?.focus();
            }
          }, 100);
        }, 50);
      } else {
        setIsLoading(false);
        isWaitingForDOMRef.current = false;
      }
    },
    [refs],
  );

  const handleSelectPhoneticSuggestion = useCallback(
    (suggestion: string) => {
      if (!focusedBlockId) return;
      const el = refs.current[focusedBlockId];
      if (!el) return;

      const selection = window.getSelection();
      if (!selection) return;

      // We replace the last character inserted with the suggestion
      const offset = getCaretCharacterOffsetWithin(el);
      const targetRange = createRangeFromGlobalOffset(el, offset - 1, offset);
      if (targetRange) {
        selection.removeAllRanges();
        selection.addRange(targetRange);
        document.execCommand("delete", false);
      }
      document.execCommand("insertText", false, suggestion);
      handleBlockTextChange(focusedBlockId, el.innerText);
      setPhoneticSuggestions([]);
    },
    [focusedBlockId, handleBlockTextChange],
  );

  const handleFocus = useCallback(
    (id: string) => {
      if (blurTimeout.current) {
        clearTimeout(blurTimeout.current);
      }
      setFocusedBlockId(id);
      const block = blocks.find((b) => b.id === id);
      if (block?.type === "CHARACTER") {
        const trimmedText = block.text.trim();
        const openParenIndex = trimmedText.lastIndexOf("(");
        const closeParenIndex = trimmedText.lastIndexOf(")");

        if (openParenIndex !== -1 && openParenIndex > closeParenIndex) {
          setShowExtensionSuggestions(true);
          setShowSuggestions(false);
        } else {
          setShowExtensionSuggestions(false);
          setShowSuggestions(openParenIndex === -1);
        }
      } else {
        setShowSuggestions(true);
        setShowExtensionSuggestions(false);
      }
    },
    [blocks],
  );

  const handleBlur = useCallback((id: string) => {
    if (document.activeElement === refs.current[id]) return;
    blurTimeout.current = setTimeout(() => {
      setShowSuggestions(false);
      setShowExtensionSuggestions(false);
    }, 200);
  }, []);

  const syncScreenplay = useCallback(() => {
    if (!onSyncWithCloudRef.current) return;

    onSyncWithCloudRef.current(blocks, sceneNumbers);
    lastSavedContent.current = ScreenplayService.serializeToSbx(blocks);
    setHasUnsavedChanges(false);
    setShowUnsavedPopover(false);
    if (popoverTimeoutRef.current) clearTimeout(popoverTimeoutRef.current);
  }, [blocks, sceneNumbers]);

  const ignoreChanges = useCallback(() => {
    setShowUnsavedPopover(false);
    if (popoverTimeoutRef.current) clearTimeout(popoverTimeoutRef.current);
  }, []);

  const loadFromUrl = useCallback(
    async (
      url: string,
      fetchOptions: RequestInit = {},
      isInitialLoad?: boolean,
    ) => {
      setIsLoading(true);
      try {
        const response = await fetch(url, fetchOptions);
        if (!response.ok) {
          throw new Error(
            `[useScreenplayEditor] Failed to fetch script. HTTP Status: ${response.status}`,
          );
        }

        let text = await response.text();
        let preParsedBlocks: Partial<Block>[] | undefined = undefined;
        let scriptContent = text;

        // 1. Detect if the content is a JSON Blocks array
        try {
          const parsed = JSON.parse(text);
          if (Array.isArray(parsed)) {
            preParsedBlocks = parsed as Partial<Block>[];
            scriptContent = ""; // Clear raw text
          }
        } catch (e) {
          // Not JSON, proceed to other formats
        }

        // 2. Detect if the content is an SBX file (HTML format)
        if (
          !preParsedBlocks &&
          (text.includes('class="divtype') ||
            url.toLowerCase().includes(".sbx"))
        ) {
          if (text.includes("&lt;div")) {
            const textarea = document.createElement("textarea");
            textarea.innerHTML = text;
            text = textarea.value;
          }

          const parser = new DOMParser();
          const doc = parser.parseFromString(text, "text/html");
          const divs = Array.from(doc.querySelectorAll("div"));
          const parsed: Partial<Block>[] = [];

          const typeMap: Record<string, BlockType> = {
            divtype0: "SCENE_HEADING",
            divtype2: "ACTION",
            divtype3: "CHARACTER",
            divtype4: "PARENTHETICAL",
            divtype5: "DIALOGUE",
            divtype6: "TRANSITION",
          };

          divs.forEach((div) => {
            let divText = div.textContent?.trim() || "";
            if (!divText) return;

            let type: BlockType = "ACTION";
            for (const className of Array.from(div.classList)) {
              if (typeMap[className]) {
                type = typeMap[className];
                break;
              }
            }

            const block: Partial<Block> = { type, text: divText };

            // Preserve the block ID if it exists to prevent unnecessary re-saves
            const idAttr = div.getAttribute("id");
            if (idAttr && idAttr.startsWith("par")) {
              block.id = idAttr.substring(3);
            }

            if (type === "SCENE_HEADING") {
              const sceneNum = div.getAttribute("data-scene");
              if (sceneNum) block.sceneNumber = sceneNum;

              let parsedText = divText;
              const typeMatch = parsedText.match(/^(INT\/EXT|INT|EXT)\.?\s+/i);
              if (typeMatch) {
                let sType = typeMatch[1].toUpperCase();
                if (!sType.endsWith(".")) sType += ".";
                block.sceneType = sType as any;
                parsedText = parsedText.substring(typeMatch[0].length).trim();
              }

              const timeMatch = parsedText.match(/\s+-\s+([^-]+)$/);
              if (timeMatch) {
                block.timeOfDay = timeMatch[1].trim().toUpperCase() as any;
                parsedText = parsedText.substring(0, timeMatch.index).trim();
              }

              block.text = parsedText;
            }

            parsed.push(block);
          });

          if (parsed.length > 0) {
            preParsedBlocks = parsed;
            scriptContent = ""; // Clear raw text if parsed successfully
          }
        }

        // Try to get a clean title from the URL
        const filename =
          url
            .split("/")
            .pop()
            ?.replace(/\.sbx$/i, "") || "Imported from URL";

        isWaitingForDOMRef.current = true;
        handleScriptImport(
          filename,
          scriptContent,
          preParsedBlocks,
          isInitialLoad,
        );
      } catch (error) {
        console.error("Failed to load screenplay from URL:", error);
        setIsLoading(false);
        isWaitingForDOMRef.current = false;
      }
    },
    [handleScriptImport],
  );

  useEffect(() => {
    if (isLoading) {
      isInitialLoadRef.current = true;
      return;
    }

    // Debounce the heavy SBX serialization and comparison to keep typing and scrolling 100% fluid!
    const handler = setTimeout(() => {
      const runSerialization = () => {
        const currentSbx = ScreenplayService.serializeToSbx(blocks);

        if (isInitialLoadRef.current) {
          if (lastSavedContent.current === null) {
            lastSavedContent.current = currentSbx;
          }
          isInitialLoadRef.current = false;
          return;
        }

        const hasActualChanges =
          lastSavedContent.current !== null &&
          currentSbx !== lastSavedContent.current;

        if (hasActualChanges) {
          setHasUnsavedChanges(true);
          if (popoverTimeoutRef.current)
            clearTimeout(popoverTimeoutRef.current);
          setShowUnsavedPopover(false);
          popoverTimeoutRef.current = setTimeout(() => {
            setShowUnsavedPopover(true);
          }, 1000);
        } else {
          setHasUnsavedChanges(false);
          setShowUnsavedPopover(false);
          if (popoverTimeoutRef.current)
            clearTimeout(popoverTimeoutRef.current);
        }
      };

      if (typeof window !== "undefined" && "requestIdleCallback" in window) {
        window.requestIdleCallback(
          () => {
            runSerialization();
          },
          { timeout: 1000 },
        );
      } else {
        runSerialization();
      }
    }, 250);

    return () => {
      clearTimeout(handler);
      if (popoverTimeoutRef.current) clearTimeout(popoverTimeoutRef.current);
    };
  }, [blocks, isLoading]);

  useEffect(() => {
    if (options?.initialUrl && options.initialUrl !== loadedUrlRef.current) {
      loadedUrlRef.current = options.initialUrl;
      loadFromUrl(options.initialUrl, options.fetchOptions, true);
    }
  }, [options?.initialUrl, options?.fetchOptions, loadFromUrl]);

  return {
    blocks,
    refs,
    focusedBlockId,
    showSuggestions,
    showExtensionSuggestions,
    characterExtensions,
    locations,
    characters,
    sceneNumbers,
    isLoading,
    handleBlockTextChange,
    handleSceneTypeChange,
    handleTimeOfDayChange,
    handleBlockTypeChange,
    handleSelectCharacterExtension,
    handleKeyDown,
    handleScriptImport,
    handleSceneNumberChange,
    handleFocus,
    handleBlur,
    handleEnhance: async (block: Block) => {
      if (isEnhancing || enhancingBlockId || !options?.enhanceContentUrl)
        return;
      setEnhancingBlockId(block.id);
      setIsEnhancing(true);
      setEnhancementSuggestion(null);

      try {
        const res = await fetch(options?.enhanceContentUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            mode: block.type.toLowerCase(),
            script: block.text,
            language: "English",
          }),
        });

        const data = await res.json();
        const content = data.data;

        setEnhancementSuggestion(content);
      } catch (error) {
        console.error("Failed to enhance block:", error);
        if (error) {
          setIsEnhancing(false);
        }
      } finally {
        setIsEnhancing(false);
      }
    },
    handleApproveEnhance: () => {
      if (enhancingBlockId && enhancementSuggestion) {
        setBlocks((bs) =>
          ScreenplayService.updateBlock(
            bs,
            enhancingBlockId,
            "text",
            enhancementSuggestion,
          ),
        );
        // Sync DOM manually if needed, or let useEffect handle it
      }
      setEnhancingBlockId(null);
      setEnhancementSuggestion(null);
    },
    handleRejectEnhance: () => {
      setEnhancingBlockId(null);
      setEnhancementSuggestion(null);
    },
    enhancingBlockId,
    enhancementSuggestion,
    isEnhancing,
    hasUnsavedChanges,
    showUnsavedPopover,
    syncScreenplay,
    ignoreChanges,
    loadFromUrl,
    currentLanguage,
    setCurrentLanguage,
    phoneticSuggestions,
    handleSelectPhoneticSuggestion,
    isLocked,
    autosaveStatus,
  };
}
